// =============================================================================
// FORM BUILDER TYPES
// =============================================================================

export type FieldType =
  | 'text'
  | 'email'
  | 'number'
  | 'phone'
  | 'textarea'
  | 'select'
  | 'multiselect'
  | 'checkbox'
  | 'radio'
  | 'date'
  | 'datetime'
  | 'file'
  | 'image'
  | 'signature'
  | 'rating'
  | 'section'
  | 'divider';

export interface FieldOption {
  label: string;
  value: string;
}

export interface ValidationRule {
  type: 'required' | 'minLength' | 'maxLength' | 'min' | 'max' | 'pattern' | 'email' | 'url';
  value?: string | number;
  message: string;
}

export interface FormField {
  id: string;
  type: FieldType;
  name: string;
  label: string;
  placeholder?: string;
  helpText?: string;
  defaultValue?: unknown;
  options?: FieldOption[];
  validation: ValidationRule[];
  width: 'full' | 'half' | 'third';
  conditional?: {
    fieldId: string;
    operator: 'equals' | 'notEquals' | 'contains' | 'isEmpty' | 'isNotEmpty';
    value?: unknown;
  };
}

export interface FormSection {
  id: string;
  title?: string;
  description?: string;
  fields: FormField[];
}

export interface Form {
  id: string;
  name: string;
  description: string | undefined;
  sections: FormSection[];
  settings: FormSettings;
  createdAt: string;
  updatedAt: string;
}

export interface FormSettings {
  submitLabel: string;
  successMessage: string;
  redirectUrl?: string;
  allowMultipleSubmissions: boolean;
  showProgressBar: boolean;
  theme: 'default' | 'minimal' | 'card';
}

// Field templates catalog
export interface FieldTemplate {
  type: FieldType;
  label: string;
  icon: string;
  description: string;
  category: 'basic' | 'advanced' | 'layout';
  defaultConfig: Partial<FormField>;
}

export const FIELD_TEMPLATES: FieldTemplate[] = [
  // Basic fields
  {
    type: 'text',
    label: 'Text',
    icon: 'Type',
    description: 'Single line text input',
    category: 'basic',
    defaultConfig: { placeholder: 'Enter text...' },
  },
  {
    type: 'email',
    label: 'Email',
    icon: 'Mail',
    description: 'Email address input',
    category: 'basic',
    defaultConfig: {
      placeholder: 'email@example.com',
      validation: [{ type: 'email', message: 'Please enter a valid email' }],
    },
  },
  {
    type: 'number',
    label: 'Number',
    icon: 'Hash',
    description: 'Numeric input',
    category: 'basic',
    defaultConfig: { placeholder: '0' },
  },
  {
    type: 'phone',
    label: 'Phone',
    icon: 'Phone',
    description: 'Phone number input',
    category: 'basic',
    defaultConfig: { placeholder: '+1 (555) 000-0000' },
  },
  {
    type: 'textarea',
    label: 'Long Text',
    icon: 'AlignLeft',
    description: 'Multi-line text area',
    category: 'basic',
    defaultConfig: { placeholder: 'Enter long text...' },
  },
  {
    type: 'select',
    label: 'Dropdown',
    icon: 'ChevronDown',
    description: 'Single selection dropdown',
    category: 'basic',
    defaultConfig: {
      options: [
        { label: 'Option 1', value: 'option1' },
        { label: 'Option 2', value: 'option2' },
      ],
    },
  },
  {
    type: 'multiselect',
    label: 'Multi Select',
    icon: 'ListChecks',
    description: 'Multiple selection',
    category: 'basic',
    defaultConfig: {
      options: [
        { label: 'Option 1', value: 'option1' },
        { label: 'Option 2', value: 'option2' },
      ],
    },
  },
  {
    type: 'checkbox',
    label: 'Checkbox',
    icon: 'CheckSquare',
    description: 'Single checkbox',
    category: 'basic',
    defaultConfig: {},
  },
  {
    type: 'radio',
    label: 'Radio Group',
    icon: 'CircleDot',
    description: 'Radio button group',
    category: 'basic',
    defaultConfig: {
      options: [
        { label: 'Option 1', value: 'option1' },
        { label: 'Option 2', value: 'option2' },
      ],
    },
  },

  // Advanced fields
  {
    type: 'date',
    label: 'Date',
    icon: 'Calendar',
    description: 'Date picker',
    category: 'advanced',
    defaultConfig: {},
  },
  {
    type: 'datetime',
    label: 'Date & Time',
    icon: 'Clock',
    description: 'Date and time picker',
    category: 'advanced',
    defaultConfig: {},
  },
  {
    type: 'file',
    label: 'File Upload',
    icon: 'Upload',
    description: 'File upload field',
    category: 'advanced',
    defaultConfig: {},
  },
  {
    type: 'image',
    label: 'Image Upload',
    icon: 'Image',
    description: 'Image upload field',
    category: 'advanced',
    defaultConfig: {},
  },
  {
    type: 'signature',
    label: 'Signature',
    icon: 'PenTool',
    description: 'Digital signature field',
    category: 'advanced',
    defaultConfig: {},
  },
  {
    type: 'rating',
    label: 'Rating',
    icon: 'Star',
    description: 'Star rating field',
    category: 'advanced',
    defaultConfig: {},
  },

  // Layout elements
  {
    type: 'section',
    label: 'Section',
    icon: 'LayoutTemplate',
    description: 'Group related fields',
    category: 'layout',
    defaultConfig: {},
  },
  {
    type: 'divider',
    label: 'Divider',
    icon: 'Minus',
    description: 'Horizontal divider',
    category: 'layout',
    defaultConfig: {},
  },
];
